import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, MapPin, Building, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

const KINDS = [
  { key: "airport",  label: "Airport"   },
  { key: "mall",     label: "Mall"      },
  { key: "hospital", label: "Hospital"  },
  { key: "school",   label: "School"    },
  { key: "stadium",  label: "Stadium"   },
  { key: "expo",     label: "Expo Hall" },
  { key: "hotel",    label: "Hotel"     },
  { key: "other",    label: "Other"     },
];

/**
 * /venue-owner — self-serve portal (Slice H++).
 * Lets any authenticated user list, create and manage their own venues
 * without needing admin access. Backend enforces owner_id === user.user_id
 * on all write endpoints.
 */
export default function VenueOwner() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    slug: "", name: "", kind: "airport",
    city: "", country: "", lat: "", lng: "",
    languages: "en",
  });

  useSeo({ title: "Manage venues — Find2Near", description: "Owner portal for venue maps." });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get("/venues/mine");
      setItems(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 401) return;      // handled by auth gate
      setError(e?.response?.data?.detail || "Failed to load your venues.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!authLoading && user) load(); }, [authLoading, user, load]);

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true); setError(null);
    try {
      const payload = {
        slug: form.slug.trim(),
        name: form.name.trim(),
        kind: form.kind,
        city: form.city.trim() || undefined,
        country: form.country.trim() || undefined,
        lat: form.lat ? Number(form.lat) : undefined,
        lng: form.lng ? Number(form.lng) : undefined,
        languages: form.languages.split(",").map(s => s.trim()).filter(Boolean),
        floors: [{ label: "Ground floor", order: 0 }],
      };
      const r = await api.post("/venues", payload);
      navigate(`/admin/venues/${r.data.slug}/edit`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Create failed. Slug must be unique.");
    } finally {
      setCreating(false);
    }
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="max-w-md mx-auto py-16 px-6 text-center" data-testid="owner-signin">
        <Building size={32} className="mx-auto text-stone-300 mb-3" />
        <div className="heading font-bold text-xl text-stone-900">Venue owner portal</div>
        <div className="text-sm text-stone-500 mt-2">
          Sign in to create and manage your airport, mall, hospital or campus map.
        </div>
        <Link to="/account" className="mt-4 inline-flex rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white text-sm font-bold px-5 py-2">
          Sign in →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="owner-portal">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#E05E36]">Venue owner</div>
        <h1 className="heading font-black text-3xl md:text-4xl text-stone-900">Your venue maps</h1>
        <p className="text-sm text-stone-600 mt-2 max-w-lg">
          Publish an interactive floor plan for any venue you run. Visitors get a kiosk-style map — you manage POIs, floors and languages from here.
        </p>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="owner-error">
          {error}
        </div>
      )}

      {/* My venues */}
      <div className="mb-8" data-testid="owner-list">
        {loading ? (
          <div className="text-stone-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-stone-500 text-sm bg-stone-50 border border-dashed border-stone-200 rounded-2xl p-6 text-center">
            You don’t own any venues yet — create your first one below.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map(v => (
              <Link
                key={v.venue_id}
                to={`/admin/venues/${v.slug}/edit`}
                className="flex items-center gap-3 bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300"
                data-testid={`owner-venue-${v.slug}`}
              >
                <div className="w-10 h-10 rounded-full bg-stone-100 grid place-items-center">
                  <Building size={16} className="text-stone-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900 truncate">{v.name}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-1">
                    <MapPin size={11} /> {[v.city, v.country].filter(Boolean).join(", ") || "Set location"} · {v.floors?.length || 0} floors
                  </div>
                </div>
                <ChevronRight size={16} className="text-stone-400" />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Create form */}
      <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="owner-create">
        <div className="heading font-bold text-lg mb-3 text-stone-900 flex items-center gap-2">
          <Plus size={16} className="text-[#E05E36]" /> Create a venue
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Slug</div>
            <input required value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="mumbai-t2" className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="owner-form-slug" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Name</div>
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Mumbai T2 Terminal" className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="owner-form-name" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Kind</div>
            <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="owner-form-kind">
              {KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Languages</div>
            <input value={form.languages} onChange={e => setForm({ ...form, languages: e.target.value })} placeholder="en, hi, vi" className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="owner-form-langs" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">City</div>
            <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="owner-form-city" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Country</div>
            <input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder="IN" className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="owner-form-country" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Lat</div>
            <input type="number" step="any" value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="owner-form-lat" />
          </label>
          <label>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Lng</div>
            <input type="number" step="any" value={form.lng} onChange={e => setForm({ ...form, lng: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2" data-testid="owner-form-lng" />
          </label>
          <div className="md:col-span-2 flex items-center gap-2">
            <button
              type="submit"
              disabled={creating || !form.slug || !form.name}
              className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] disabled:opacity-50 text-white text-sm font-bold px-5 py-2 inline-flex items-center gap-1"
              data-testid="owner-form-submit"
            >
              <Plus size={14} /> {creating ? "Creating…" : "Create venue"}
            </button>
            <div className="text-xs text-stone-500">You will be redirected to the POI editor after creation.</div>
          </div>
        </form>
      </div>
    </div>
  );
}
